// --- Imports ---
import { App } from "antd";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPointInventoryGetThunk } from "tm-shared/api";
import { Point, TaskRejectProducts, TaskRoute } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { useCatalog, useFilters } from "@/domains/product";
import { SVScanButton } from "@/features/SupervisorScan";
import { useQueryFilter } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
    usePointInventorySelector,
    usePointPriceListSelector,
} from "@/hooks/redux";
import { useTaskPayloadSelector } from "@/hooks/redux/useProgressSelectors";
import { ConfirmActionBtn } from "@/pages/WorkPlan/Tasks/components/ConfirmActionBtn";
import { Loading } from "@/shared/feedback/Loading";
import { Header } from "@/shared/layout";
import { HeaderFilterButton } from "@/shared/navigation/HeaderFilterButton";
import { Meta } from "@/shared/seo";

import { RejectFilterDrawer } from "./components/RejectFilterDrawer";
import { RejectProductsList } from "./components/RejectProductsList";
import { RejectProductsTopSection } from "./components/RejectProductsTopSection";
import { useTaskCompletion } from "./hooks";
import { StyledMainContainer } from "./RejectProducts.styled";
import { StrictRejectProduct } from "./RejectProducts.types";

// --- Types ---
interface RejectProductsPageProps {
    point: Point;
    task: TaskRejectProducts;
}

// --- Component ---
export const RejectProductsPage: FC<RejectProductsPageProps> = ({
    point,
    task,
}) => {
    // --- App hooks ---
    const { notification } = App.useApp();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { products } = useCatalog();

    // --- Derived identifiers ---
    const taskId = task.id;
    const pointId = point.id;

    // --- Store selectors ---
    const offlineMode = useOfflineModeSelector();
    const queryMap = useAppSelector(
        (state) => state.ui.rejectProducts.queryMap,
    );
    const selectedFilters = useAppSelector(
        (state) => state.ui.rejectProducts.selectedFilters,
    );
    const currentInventory = usePointInventorySelector(pointId);
    const currentPriceList = usePointPriceListSelector(pointId);
    const rejectProductsList = useTaskPayloadSelector(
        TaskRoute.RejectProducts,
        taskId,
    );

    // --- Derived values ---
    const normalizedInventory = useMemo(
        () => currentInventory ?? {},
        [currentInventory],
    );
    const normalizedRejectProducts = useMemo(
        () => rejectProductsList ?? {},
        [rejectProductsList],
    );
    const inventoryCount = useMemo(
        () => Object.keys(normalizedInventory).length,
        [normalizedInventory],
    );
    const filterCount = useMemo(
        () => Object.values(selectedFilters ?? {}).length,
        [selectedFilters],
    );
    const initialRejectProducts: Record<string, StrictRejectProduct> = useMemo(
        () =>
            Object.entries(normalizedInventory).reduce<
                Record<string, StrictRejectProduct>
            >((acc, [barcode, count]) => {
                const product = products[barcode.trim()];

                if (product) {
                    acc[barcode] = {
                        ...product,
                        price: currentPriceList?.[barcode]?.price ?? -1,
                        max: count,
                    };
                }
                return acc;
            }, {}),
        [currentPriceList, normalizedInventory, products],
    );

    // --- Local state ---
    const [open, setOpen] = useState(false);

    // --- Derived filtered list ---
    const { filteredProducts, availableFilters } = useFilters({
        products: initialRejectProducts,
        inventoryList: normalizedInventory,
        filters: selectedFilters ?? {},
    });

    // --- Derived filters ---
    const searchQuery = useMemo(
        () => queryMap?.[taskId] ?? "",
        [queryMap, taskId],
    );

    const searchFilteredProducts = useQueryFilter<StrictRejectProduct>(
        filteredProducts,
        searchQuery,
        [(p) => p?.name, (p) => p?.barcode],
    );
    const productsListLength = searchFilteredProducts.length;

    // --- Task hooks ---
    const { loading, handleConfirm: handleConfirmTask } = useTaskCompletion({
        rejectProductsList: normalizedRejectProducts,
        task,
        pointId,
    });

    // --- Handlers ---
    const handleConfirmUpdateRemaining = useCallback(() => {
        navigate(
            buildRoutePath(ROUTES.UpdateProductBalancesTask, {
                taskId,
                pointId,
            }),
        );
    }, [pointId, taskId, navigate]);

    const openDrawer = useCallback(() => setOpen(true), []);

    const onFilterClick = () =>
        productsListLength > 0
            ? openDrawer()
            : notification.error({
                  title: "Немає товарів для повернення, щоб їх відфільтрувати",
              });

    // --- Effects ---
    useEffect(() => {
        if (!offlineMode) {
            dispatch(getPointInventoryGetThunk({ pointId }));
        }
    }, [dispatch, pointId, offlineMode]);

    // --- Render ---
    return (
        <>
            <Meta title="Повернення товару" />

            <Header
                title="Повернення товару"
                back
                rightActions={
                    <>
                        <SVScanButton pointId={pointId} taskId={taskId} />
                        <HeaderFilterButton
                            onClick={onFilterClick}
                            filterCount={filterCount}
                        />
                    </>
                }
            />

            <RejectProductsTopSection
                rejectProductsList={normalizedRejectProducts}
                taskId={taskId}
                handleConfirmUpdateRemaining={handleConfirmUpdateRemaining}
            />

            <StyledMainContainer className="reject-product-list">
                <RejectProductsList
                    products={searchFilteredProducts}
                    taskId={taskId}
                    hasProducts={inventoryCount > 0}
                    rejectProductsList={normalizedRejectProducts}
                />

                {productsListLength > 0 && (
                    <ConfirmActionBtn
                        onConfirm={handleConfirmTask}
                        title="Завершити задачу"
                        description="Ви впевнені що хочете завершити виконання задачі?"
                        loading={loading}
                    />
                )}
            </StyledMainContainer>

            <RejectFilterDrawer
                open={open}
                setOpen={setOpen}
                products={initialRejectProducts}
                filteredProducts={filteredProducts}
                availableFilters={availableFilters}
            />

            {loading && <Loading />}
        </>
    );
};
